import { AxiosError } from 'axios';
import {
  InspectionRequest,
  CreateInspectionRequestPayload,
  InspectionSchedule,
  ScheduleInspectionPayload,
  InspectionAssignment,
  AssignInspectionPayload,
  InspectionReason,
  InspectionItem,
  UpdateItemAddressPayload,
  UpdateInspectionTypePayload,
  PerformCheckinPayload,
  InspectionCheckin,
  InspectionIssue,
  CreateInspectionIssuePayload,
  UpdateInspectionIssuePayload,
  ResolveInspectionIssuePayload,
  AddIssueCommentPayload,
  IssueAttachment,
  GoogleCalendarStatus,
  CheckDuplicateLocationPayload,
  CheckDuplicateLocationResponse,
  InspectionExportRow,
  InspectionExportResult,
  ExportFilterOptions,
} from './types/inspection.types';
import { inspectionClient } from '@/config/api';
import { PaginatedResponse } from './types/api.types';
import { apiErrorHandler } from '@/helpers/apiErrorHandler';

export class InspectionService {
  async createInspectionRequest(
    payload: CreateInspectionRequestPayload
  ): Promise<InspectionRequest> {
    try {
      const response = await inspectionClient.post<InspectionRequest>(
        '/api/InspectionRequest',
        payload
      );
      return response.data;
    } catch (error: unknown) {
      apiErrorHandler(error, 'Erro ao criar solicitação de inspeção');
    }
  }

  async getInspectionRequestById(id: string): Promise<InspectionRequest> {
    const response = await inspectionClient.get<InspectionRequest>(`/api/InspectionRequest/${id}`);
    return response.data;
  }



  async getAllInspectionRequests(params?: {
    pageNumber?: number;
    pageSize?: number;
    statuses?: string[];
    inspectionTypeIds?: string[];
    inspectionReasonIds?: string[];
    providerIds?: string[];
    inspectorIds?: string[];
    states?: string[];
    cities?: string[];
    isScheduled?: boolean;
    startDate?: string;
    endDate?: string;
  }, options?: {
    // Timeout específico desta chamada (ms). Usado pela exportação do relatório,
    // que legitimamente demora mais que o timeout global de API em bases grandes.
    timeoutMs?: number;
  }): Promise<PaginatedResponse<InspectionRequest>> {
    const queryParams = new URLSearchParams();

    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.statuses && params.statuses.length > 0) {
      params.statuses.forEach(status => queryParams.append('statuses', status));
    }
    if (params?.inspectionTypeIds && params.inspectionTypeIds.length > 0) {
      params.inspectionTypeIds.forEach(id => queryParams.append('inspectionTypeIds', id));
    }
    if (params?.inspectionReasonIds && params.inspectionReasonIds.length > 0) {
      params.inspectionReasonIds.forEach(id => queryParams.append('inspectionReasonIds', id));
    }
    if (params?.providerIds && params.providerIds.length > 0) {
      params.providerIds.forEach(id => queryParams.append('providerIds', id));
    }
    if (params?.inspectorIds && params.inspectorIds.length > 0) {
      params.inspectorIds.forEach(id => queryParams.append('inspectorIds', id));
    }
    if (params?.states && params.states.length > 0) {
      params.states.forEach(state => queryParams.append('states', state));
    }
    if (params?.cities && params.cities.length > 0) {
      params.cities.forEach(city => queryParams.append('cities', city));
    }
    if (params?.isScheduled !== undefined && params?.isScheduled !== null) queryParams.append('isScheduled', params.isScheduled.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `/api/InspectionRequest${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await inspectionClient.get<PaginatedResponse<InspectionRequest>>(
      url,
      options?.timeoutMs ? { timeout: options.timeoutMs } : undefined
    );

    return response.data;
  }

  /**
   * Exportação do relatório Panorama: uma única chamada que devolve linhas achatadas
   * montadas no backend com consultas em lote (sem paginação nem DTO completo).
   */
  async getInspectionExportRows(params?: {
    statuses?: string[];
    inspectionTypeIds?: string[];
    inspectionReasonIds?: string[];
    providerIds?: string[];
    inspectorIds?: string[];
    reportAuthorIds?: string[];
    states?: string[];
    cities?: string[];
    isScheduled?: boolean;
    startDate?: string;
    endDate?: string;
  }, options?: { timeoutMs?: number }): Promise<InspectionExportResult> {
    const queryParams = new URLSearchParams();

    if (params?.statuses?.length) params.statuses.forEach(s => queryParams.append('statuses', s));
    if (params?.inspectionTypeIds?.length) params.inspectionTypeIds.forEach(id => queryParams.append('inspectionTypeIds', id));
    if (params?.inspectionReasonIds?.length) params.inspectionReasonIds.forEach(id => queryParams.append('inspectionReasonIds', id));
    if (params?.providerIds?.length) params.providerIds.forEach(id => queryParams.append('providerIds', id));
    if (params?.inspectorIds?.length) params.inspectorIds.forEach(id => queryParams.append('inspectorIds', id));
    if (params?.reportAuthorIds?.length) params.reportAuthorIds.forEach(id => queryParams.append('reportAuthorIds', id));
    if (params?.states?.length) params.states.forEach(s => queryParams.append('states', s));
    if (params?.cities?.length) params.cities.forEach(c => queryParams.append('cities', c));
    if (params?.isScheduled !== undefined && params?.isScheduled !== null) queryParams.append('isScheduled', params.isScheduled.toString());
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `/api/InspectionRequest/export${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    const response = await inspectionClient.get<InspectionExportRow[]>(
      url,
      options?.timeoutMs ? { timeout: options.timeoutMs } : undefined
    );

    // O backend sinaliza em headers quando cortou o resultado no teto de linhas,
    // para o usuário ser avisado em vez de receber uma planilha incompleta em silêncio.
    const truncated = String(response.headers?.['x-export-truncated'] ?? '').toLowerCase() === 'true';
    const maxRows = Number(response.headers?.['x-export-max-rows']);

    return {
      rows: response.data ?? [],
      truncated,
      maxRows: Number.isFinite(maxRows) && maxRows > 0 ? maxRows : undefined,
    };
  }

  /**
   * Opções dos filtros "Inspetor" e "Elaborador do Laudo" do modal de exportação, derivadas
   * dos dados (direcionamentos ativos e transmissões de laudo), inclusive usuários inativos.
   */
  async getExportFilterOptions(options?: { timeoutMs?: number }): Promise<ExportFilterOptions> {
    const response = await inspectionClient.get<ExportFilterOptions>(
      '/api/InspectionRequest/export/filter-options',
      // O backend resolve o nome de cada inspetor/elaborador no user-service; em bases
      // grandes isso pode passar do timeout global, e aí o modal cairia no cadastro.
      { timeout: options?.timeoutMs ?? 120000 },
    );
    return response.data;
  }

  async getMyInspections(params?: {
    pageNumber?: number;
    pageSize?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
  }): Promise<PaginatedResponse<InspectionRequest>> {
    const queryParams = new URLSearchParams();

    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.status) queryParams.append('status', params.status);
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);

    const url = `/api/InspectionRequest/my-inspections${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await inspectionClient.get<PaginatedResponse<InspectionRequest>>(url);

    return response.data;
  }

  async toggleFavorite(inspectionId: string): Promise<{ isFavorite: boolean }> {
    const response = await inspectionClient.patch<{ isFavorite: boolean }>(
      `/api/InspectionRequest/${inspectionId}/toggle-favorite`
    );
    return response.data;
  }

  async getInspections(params?: {
    page?: number;
    pageSize?: number;
    statuses?: string[];
    startDate?: string;
    endDate?: string;
    searchCode?: string;
    providerId?: string;
    inspectorId?: string;
    hasOpenIssues?: boolean;
    inspectionTypeId?: string;
    propertyTypeId?: string;
    inspectionReasonId?: string;
    city?: string;
    channel?: string;
  }): Promise<{ items: InspectionItem[]; totalCount: number }> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('pageNumber', params.page.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.statuses && params.statuses.length > 0) {
      params.statuses.forEach(status => queryParams.append('statuses', status));
    }
    if (params?.startDate) queryParams.append('startDate', params.startDate);
    if (params?.endDate) queryParams.append('endDate', params.endDate);
    if (params?.searchCode) queryParams.append('searchCode', params.searchCode);
    if (params?.providerId) queryParams.append('providerId', params.providerId);
    if (params?.inspectorId) queryParams.append('inspectorId', params.inspectorId);
    if (params?.hasOpenIssues !== undefined) queryParams.append('hasOpenIssues', params.hasOpenIssues.toString());
    if (params?.inspectionTypeId) queryParams.append('inspectionTypeId', params.inspectionTypeId);
    if (params?.propertyTypeId) queryParams.append('propertyTypeId', params.propertyTypeId);
    if (params?.inspectionReasonId) queryParams.append('inspectionReasonId', params.inspectionReasonId);
    if (params?.city) queryParams.append('city', params.city);
    if (params?.channel) queryParams.append('channel', params.channel);

    const url = `/api/inspection-items${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await inspectionClient.get<PaginatedResponse<InspectionItem>>(url);

    return {
      items: response.data.items || [],
      totalCount: response.data.totalCount || 0,
    };
  }

  /**
   * Lista valores distintos (paginados + busca) de um campo de texto livre
   * (hoje 'city' e 'channel') para o dropdown de filtro de coluna server-side.
   * Os filtros já ativos (statuses/provider) são repassados para manter as
   * opções coerentes com o contexto visível ao usuário.
   */
  async getFilterOptions(params: {
    field: 'city' | 'channel';
    search?: string;
    pageNumber?: number;
    pageSize?: number;
    statuses?: string[];
    providerId?: string;
    inspectorId?: string;
  }): Promise<{ items: { value: string; label: string }[]; totalCount: number }> {
    const queryParams = new URLSearchParams();
    queryParams.append('field', params.field);
    if (params.search) queryParams.append('search', params.search);
    queryParams.append('pageNumber', (params.pageNumber ?? 1).toString());
    queryParams.append('pageSize', (params.pageSize ?? 20).toString());
    if (params.statuses && params.statuses.length > 0) {
      params.statuses.forEach(status => queryParams.append('statuses', status));
    }
    if (params.providerId) queryParams.append('providerId', params.providerId);
    if (params.inspectorId) queryParams.append('inspectorId', params.inspectorId);

    const url = `/api/inspection-items/filter-options?${queryParams.toString()}`;
    const response = await inspectionClient.get<{ items: { value: string; label: string }[]; totalCount: number }>(url);

    return {
      items: response.data.items || [],
      totalCount: response.data.totalCount || 0,
    };
  }

  async scheduleInspectionItem(
    inspectionItemId: string,
    payload: ScheduleInspectionPayload
  ): Promise<InspectionSchedule> {
    const response = await inspectionClient.post<InspectionSchedule>(
      `/api/InspectionSchedule/item/${inspectionItemId}`,
      payload
    );
    return response.data;
  }

  async getScheduleById(scheduleId: string): Promise<InspectionSchedule> {
    const response = await inspectionClient.get<InspectionSchedule>(
      `/api/InspectionSchedule/${scheduleId}`
    );
    return response.data;
  }

  async getSchedulesByInspectionItem(inspectionItemId: string): Promise<InspectionSchedule[]> {
    const response = await inspectionClient.get<InspectionSchedule[]>(
      `/api/InspectionSchedule/item/${inspectionItemId}`
    );
    return response.data;
  }

  async assignInspectionItemToProvider(
    inspectionItemId: string,
    payload: AssignInspectionPayload
  ): Promise<InspectionAssignment> {
    const response = await inspectionClient.post<InspectionAssignment>(
      `/api/InspectionAssignment/item/${inspectionItemId}`,
      payload
    );
    return response.data;
  }

  async assignInspectionItemDirectlyToInspector(
    inspectionItemId: string,
    payload: { inspectorId: string; notes: string }
  ): Promise<InspectionAssignment> {
    const response = await inspectionClient.post<InspectionAssignment>(
      `/api/InspectionAssignment/item/${inspectionItemId}/direct-to-inspector`,
      payload
    );
    return response.data;
  }

  async getAssignmentById(assignmentId: string): Promise<InspectionAssignment> {
    const response = await inspectionClient.get<InspectionAssignment>(
      `/api/InspectionAssignment/${assignmentId}`
    );
    return response.data;
  }

  async getAssignmentsByInspectionItem(inspectionItemId: string): Promise<InspectionAssignment[]> {
    const response = await inspectionClient.get<InspectionAssignment[]>(
      `/api/InspectionAssignment/item/${inspectionItemId}`
    );
    return response.data;
  }

  async getActiveAssignment(inspectionItemId: string): Promise<InspectionAssignment> {
    const response = await inspectionClient.get<InspectionAssignment>(
      `/api/InspectionAssignment/item/${inspectionItemId}/active`
    );
    return response.data;
  }

  async getMyAssignments(): Promise<InspectionAssignment[]> {
    const response = await inspectionClient.get<InspectionAssignment[]>(
      '/api/InspectionAssignment/my-assignments'
    );
    return response.data;
  }

  async getAllInspectionReasons(): Promise<InspectionReason[]> {
    const response = await inspectionClient.get<InspectionReason[]>('/api/InspectionReason');
    return response.data;
  }

  async getActiveInspectionReasons(): Promise<InspectionReason[]> {
    const response = await inspectionClient.get<InspectionReason[]>('/api/InspectionReason/active');
    return response.data;
  }

  async getInspectionReasonById(id: string): Promise<InspectionReason> {
    const response = await inspectionClient.get<InspectionReason>(`/api/InspectionReason/${id}`);
    return response.data;
  }

  async getInspectionItemById(itemId: string): Promise<InspectionItem> {
    const response = await inspectionClient.get<InspectionItem>(`/api/inspection-items/${itemId}`);
    return response.data;
  }

  async getInspectionItemsByRequest(inspectionRequestId: string): Promise<InspectionItem[]> {
    const response = await inspectionClient.get<InspectionItem[]>(
      `/api/inspection-items/inspection/${inspectionRequestId}`
    );
    return response.data;
  }

  async finalizeInspectionItem(itemId: string): Promise<InspectionItem> {
    const response = await inspectionClient.post<InspectionItem>(
      `/api/inspection-items/${itemId}/finalize`
    );
    return response.data;
  }

  async cancelInspectionItem(itemId: string, data: {reason: string; additionalInfo?: string} ): Promise<InspectionItem> {
    const response = await inspectionClient.post<InspectionItem>(
      `/api/inspection-items/${itemId}/cancel`, data
    );
    return response.data;
  }

  /**
   * Marca o checklist como preenchido e muda o status para AguardandoRevisaoPrestadora (13)
   * NOVO FLUXO: Inspetor só faz checklist, Prestadora/Banco geram o laudo
   */
  async completeChecklist(itemId: string): Promise<InspectionItem> {
    const response = await inspectionClient.post<InspectionItem>(
      `/api/inspection-items/${itemId}/complete-checklist`
    );
    return response.data;
  }

  async updateItemAddress(
    itemId: string,
    payload: UpdateItemAddressPayload
  ): Promise<InspectionItem> {
    const response = await inspectionClient.patch<InspectionItem>(
      `/api/inspection-items/${itemId}/address`,
      payload
    );
    return response.data;
  }

  async updateItemPropertyData(
    itemId: string,
    payload: {
      iptuNumber?: string;
      registrationNumber?: string;
      registryOfficeNumber?: string;
      landArea?: number;
      builtArea?: number;
      privateArea?: number;
    }
  ): Promise<InspectionItem> {
    const response = await inspectionClient.patch<InspectionItem>(
      `/api/inspection-items/${itemId}/property-data`,
      payload
    );
    return response.data;
  }

  async updatePreliminaryCalculations(
    itemId: string,
    data: string | null
  ): Promise<InspectionItem> {
    const response = await inspectionClient.patch<InspectionItem>(
      `/api/inspection-items/${itemId}/preliminary-calculations`,
      { preliminaryCalculationsData: data }
    );
    return response.data;
  }

  async updateItemInspectionType(
    itemId: string,
    payload: UpdateInspectionTypePayload
  ): Promise<InspectionItem> {
    const response = await inspectionClient.patch<InspectionItem>(
      `/api/inspection-items/${itemId}/inspection-type`,
      payload
    );
    return response.data;
  }

  async addAttachmentToItem(itemId: string, attachment: any): Promise<any> {
    const response = await inspectionClient.post(
      `/api/inspection-items/${itemId}/attachments`,
      attachment
    );
    return response.data;
  }

  async performCheckin(itemId: string, payload: PerformCheckinPayload): Promise<InspectionCheckin> {
    const response = await inspectionClient.post<InspectionCheckin>(
      `/api/InspectionCheckin/item/${itemId}`,
      payload
    );
    return response.data;
  }

  async getCheckinByItem(itemId: string): Promise<InspectionCheckin> {
    const response = await inspectionClient.get<InspectionCheckin>(
      `/api/InspectionCheckin/item/${itemId}`
    );
    return response.data;
  }

  async getCheckinsByInspectionRequest(inspectionRequestId: string): Promise<InspectionCheckin[]> {
    const response = await inspectionClient.get<InspectionCheckin[]>(
      `/api/InspectionCheckin/inspection/${inspectionRequestId}`
    );
    return response.data;
  }

  async createInspectionIssue(payload: CreateInspectionIssuePayload): Promise<InspectionIssue> {
    const response = await inspectionClient.post<InspectionIssue>(
      '/api/inspection-issues',
      payload
    );
    return response.data;
  }

  async getInspectionIssueById(issueId: string): Promise<InspectionIssue> {
    const response = await inspectionClient.get<InspectionIssue>(
      `/api/inspection-issues/${issueId}`
    );
    return response.data;
  }

  async getInspectionIssuesByRequest(inspectionRequestId: string): Promise<InspectionIssue[]> {
    const response = await inspectionClient.get<InspectionIssue[]>(
      `/api/inspection-issues/inspection-request/${inspectionRequestId}`
    );
    return response.data;
  }

  async getInspectionIssuesByStatus(status: number): Promise<InspectionIssue[]> {
    const response = await inspectionClient.get<InspectionIssue[]>(
      `/api/inspection-issues/status/${status}`
    );
    return response.data;
  }

  async updateInspectionIssue(
    issueId: string,
    payload: UpdateInspectionIssuePayload
  ): Promise<InspectionIssue> {
    const response = await inspectionClient.patch<InspectionIssue>(
      `/api/inspection-issues/${issueId}`,
      payload
    );
    return response.data;
  }

  async resolveInspectionIssue(
    issueId: string,
    payload: ResolveInspectionIssuePayload
  ): Promise<InspectionIssue> {
    const response = await inspectionClient.post<InspectionIssue>(
      `/api/inspection-issues/${issueId}/resolve`,
      payload
    );
    return response.data;
  }

  async deleteInspectionIssue(issueId: string): Promise<void> {
    await inspectionClient.delete(`/api/inspection-issues/${issueId}`);
  }

  async addIssueComment(issueId: string, payload: AddIssueCommentPayload): Promise<any> {
    const response = await inspectionClient.post(
      `/api/inspection-issues/${issueId}/comments`,
      payload
    );
    return response.data;
  }

  async addIssueAttachment(issueId: string, attachment: IssueAttachment): Promise<any> {
    const response = await inspectionClient.post(
      `/api/inspection-issues/${issueId}/attachments`,
      attachment
    );
    return response.data;
  }

  async connectGoogleCalendar(inspectorId: string): Promise<{ authorizationUrl: string }> {
    const response = await inspectionClient.get<{ authorizationUrl: string }>(
      `/api/google-calendar/connect?inspectorId=${inspectorId}`
    );
    return response.data;
  }

  async getGoogleCalendarStatus(inspectorId: string): Promise<GoogleCalendarStatus> {
    const response = await inspectionClient.get<GoogleCalendarStatus>(
      `/api/google-calendar/status/${inspectorId}`
    );
    return response.data;
  }

  async disconnectGoogleCalendar(inspectorId: string): Promise<void> {
    await inspectionClient.post(`/api/google-calendar/disconnect/${inspectorId}`);
  }

  async healthCheck(): Promise<{ status: string }> {
    const response = await inspectionClient.get<{ status: string }>('/health');
    return response.data;
  }

  async checkDuplicateLocation(
    payload: CheckDuplicateLocationPayload
  ): Promise<CheckDuplicateLocationResponse> {
    const response = await inspectionClient.post<CheckDuplicateLocationResponse>(
      '/api/inspection-items/check-duplicate-location',
      payload
    );
    return response.data;
  }

  // Scraping Job methods
  async getScrapingJobByItemId(itemId: string): Promise<ScrapingJob | null> {
    try {
      const response = await inspectionClient.get<ScrapingJob>(
        `/api/inspection-items/${itemId}/scraping-job`
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getScrapedPropertiesByItemId(itemId: string): Promise<ScrapedPropertiesResult | null> {
    try {
      const response = await inspectionClient.get<ScrapedPropertiesResult>(
        `/api/inspection-items/${itemId}/scraped-properties`
      );
      return response.data;
    } catch (error: unknown) {
      if (error instanceof AxiosError && error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async retryScrapingJob(itemId: string, params?: RetryScrapingJobParams): Promise<ScrapingJob> {
    const response = await inspectionClient.post<ScrapingJob>(
      `/api/inspection-items/${itemId}/scraping-job/retry`,
      params || {}
    );
    return response.data;
  }

  /**
   * Busca o histórico unificado de movimentações do item de inspeção
   * Inclui workflow (direcionamento, agendamento, checkin, etc) e histórico do laudo
   */
  async getUnifiedHistory(inspectionItemId: string): Promise<UnifiedHistoryResponse> {
    const response = await inspectionClient.get<UnifiedHistoryResponse>(
      `/api/inspection-items/${inspectionItemId}/history`
    );
    return response.data;
  }

  /**
   * Busca avaliações históricas (laudos aprovados) próximas a uma coordenada
   * Usado para mostrar pins brancos de referência no mapa de scraping
   */
  async getHistoricalEvaluations(
    params: HistoricalEvaluationsParams
  ): Promise<HistoricalEvaluationsResponse> {
    const queryParams = new URLSearchParams();
    queryParams.append('latitude', params.latitude.toString());
    queryParams.append('longitude', params.longitude.toString());
    if (params.radiusMeters) queryParams.append('radiusMeters', params.radiusMeters.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const response = await inspectionClient.get<HistoricalEvaluationsResponse>(
      `/api/inspection-items/historical-evaluations?${queryParams.toString()}`
    );
    return response.data;
  }
}

// Scraping types
export interface ScrapingJob {
  id: string;
  inspectionItemId: string;
  status: ScrapingJobStatus;
  statusDescription: string;
  errorMessage?: string;
  totalPropertiesFound: number;
  searchAddress: string;
  areaMin?: number;
  areaMax?: number;
  radiusMeters?: number;
  createdAt: string;
  startedAt?: string;
  completedAt?: string;
}

export enum ScrapingJobStatus {
  Pending = 0,
  Processing = 1,
  Completed = 2,
  Failed = 3,
  Cancelled = 4,
}

export interface ScrapedProperty {
  id: string;
  scrapingJobId: string;
  externalId: string;
  source: string;
  title: string;
  address: string;
  district?: string;
  city?: string;
  state?: string;
  price?: number;
  areaUtil?: number;
  areaTotal?: number;
  bedrooms?: number;
  bathrooms?: number;
  parkingSpaces?: number;
  latitude?: number;
  longitude?: number;
  distanceMeters?: number;
  score?: number;
  url?: string;
  imageUrl?: string;
  amenities?: string;
  propertyType?: string;
  scrapedAt: string;
}

export interface ScrapedPropertiesResult {
  job: ScrapingJob;
  properties: ScrapedProperty[];
}

export interface RetryScrapingJobParams {
  radiusMeters?: number;
  areaMin?: number;
  areaMax?: number;
}

// Unified History types
export interface UnifiedHistoryItem {
  id: string;
  inspectionItemId: string;
  action: string;
  description: string;
  notes?: string;
  performedByUserName: string;
  performedByUserRole: string;
  performedAt: string;
  targetProviderName?: string;
  targetInspectorName?: string;
  source: 'workflow' | 'report';
  statusBadge?: string;
  statusColor?: string;
}

export interface UnifiedHistoryResponse {
  items: UnifiedHistoryItem[];
  totalCount: number;
}

// Historical evaluation types
export interface HistoricalEvaluation {
  inspectionItemId: string;
  proposalNumber: string;
  address: string;
  district: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  // Dados do Imóvel
  propertyType: string;
  propertyTypeDescription?: string;
  privateArea: number;
  builtArea?: number;
  landArea?: number;
  bedrooms?: number;
  bathrooms?: number;
  parkingSpaces?: number;
  apparentAge?: number;
  conservationState?: string;
  finishingStandard?: string;
  // Valores da Avaliação
  evaluatedValue: number;
  evaluatedValuePerM2: number;
  // Metadados
  completedAt: string;
  inspectionType: string;
}

export interface HistoricalEvaluationsParams {
  latitude: number;
  longitude: number;
  radiusMeters?: number;
  limit?: number;
}

export interface HistoricalEvaluationsResponse {
  evaluations: HistoricalEvaluation[];
  totalCount: number;
}

export const inspectionService = new InspectionService();
